/**
 * OperationManager tracks AbortControllers for async operations.
 *
 * Key responsibilities:
 * - Track active operations per item ID
 * - Provide AbortSignal for cancellation
 * - Cancel operations when needed
 */
export class OperationManager {
  private readonly controllers = new Map<string, AbortController>();

  /**
   * Start a new operation, cancelling any existing one for this item.
   *
   * @param itemId The ID of the item being operated on
   * @returns AbortSignal for the new operation
   */
  public start(itemId: string): AbortSignal {
    // Cancel any existing operation for this item
    this.cancel(itemId);

    const controller = new AbortController();
    this.controllers.set(itemId, controller);
    return controller.signal;
  }

  /**
   * Cancel the operation for a specific item.
   *
   * @param itemId The ID of the item whose operation should be cancelled
   */
  public cancel(itemId: string): void {
    const controller = this.controllers.get(itemId);
    if (controller) {
      controller.abort();
      this.controllers.delete(itemId);
    }
  }

  /**
   * Mark an operation as complete (remove from tracking).
   *
   * @param itemId The ID of the item whose operation completed
   */
  public complete(itemId: string): void {
    this.controllers.delete(itemId);
  }

  public cancelAll(): void {
    for (const controller of this.controllers.values()) {
      controller.abort();
    }
    this.controllers.clear();
  }
}
